// @ts-nocheck
import { routesMap } from 'app/Router';
import axios from 'axios';
import { toast } from 'components/RevxMessage';
import { EventSourcePolyfill } from 'event-source-polyfill';
import i18n from 'i18n/config';
import qs from 'qs';
import { SYSTEM_UNAVAILABLE_STATUS_KEY } from './constants';
import { lookupErrorCode, RevxErrorMessage } from './errorCodes';
import { shouldRedirectToLoginOn401 } from './canvaSso';
import {
  MEASUREMENT_UNIT_PARAM,
  MeasurementUnitEnum,
} from 'domains/measurementDomain';
import { getEffectiveUnit } from './measurement';

const methods = ['get', 'head', 'post', 'put', 'delete', 'options', 'patch'];
const paramsMethods = ['get'];
const ResponseOKCode = 0;
const Authorization = 'Authorization';
const unavailablePath = routesMap?.unavailable?.path || '/unavailable';
const isUnavailablePage = window.location.pathname.startsWith(unavailablePath);

type Params = Object | undefined | FormData;

const defaultHeaders = {
  'Access-Control-Allow-Credentials': true,
  'Access-Control-Allow-Origin': '*',
  'Content-Type': 'application/json',
};

class Api {
  constructor() {
    methods.forEach(
      (method) =>
        (this[method] = (path, data = {}, headers = {}, cfg = {}) =>
          new Promise((resolve, reject) => {
            const config = {
              withCredentials: true, // send cookies (e.g. oauth_authorize) on cross-origin requests to the API
              headers: {
                ...defaultHeaders,
                ...headers,
              },
              ...cfg,
            };
            // axios >= 1.x turns a FormData payload into JSON when the caller
            // declares Content-Type: application/json (see formDataToJSON), which
            // silently drops the file. Let the browser set the multipart boundary.
            if (typeof FormData !== 'undefined' && data instanceof FormData) {
              delete config.headers['Content-Type'];
            }

            data =
              paramsMethods.indexOf(method) !== -1
                ? { params: data, ...config }
                : data;

            if (
              config.headers['Content-Type'] ===
              'application/x-www-form-urlencoded'
            ) {
              data = qs.stringify(data);
            }

            this.doFetch(method, path, data, config, resolve, reject);
          }))
    );
  }

  doFetch(method, path, requestData, config, resolve, reject) {
    // if (!navigator.onLine) {
    //   toast.error(i18n.t('errors.offline'));
    //   return;
    // }
    config.headers.Authorization = localStorage.getItem(Authorization) ?? '';

    const orgId = localStorage.getItem('org');
    if (orgId && !config.headers['X-Tenant-ID']) {
      config.headers['X-Tenant-ID'] = orgId;
    }

    const date = new Date();
    config.headers['timezoneOffset'] = -(date.getTimezoneOffset() / 60);

    let payload = requestData;

    // Nothing is stamped here on purpose. This used to add `enteredUnit: getEffectiveUnit()` to
    // every write, which declared a unit on behalf of payloads that had no idea what it meant.
    //
    // Most write payloads are `{...record, ...formValues}`: the form's own fields are in the unit
    // it was filled in, and every other field is a stored base value being handed straight back.
    // A blanket declaration applies to all of them. That is how saving an org while viewing in
    // metric wrote its 0.125in bleed as 0.125mm - the org form never displayed a bleed, never
    // converted one, and still told the API its inches were millimetres.
    //
    // A form that actually converted its values to the unit it displays says so itself; a form
    // that did not says nothing, and the API takes the numbers as already base. See `EnteredIn`
    // on the API side.

    if (method === 'delete') {
      payload = { data: requestData, headers: config.headers };
    }

    axios[method](buildPath(path), payload, config)
      .then(({ data }) => {
        if (
          data.code === ResponseOKCode &&
          String(data?.data || '')
            .toLowerCase()
            .indexOf('fail') === -1 &&
          String(data?.data || '').indexOf('SERVICE_UNAVAILABLE') === -1
        ) {
          localStorage.setItem(SYSTEM_UNAVAILABLE_STATUS_KEY, false);
          if (isUnavailablePage) {
            window.location.href = window.location.origin;
          }
          resolve(data.data);
        } else {
          if (
            config.useGlobalErrorHandler !== false &&
            config.headers.useGlobalErrorHandler !== false
          ) {
            this.showError(
              {
                errorCode: lookupErrorCode(data.code),
                originalMessage: data.msg,
              },
              config.showOriginalError
            );
          }
          reject({
            errorCode: lookupErrorCode(data.code),
            originalMessage: data.msg,
            message: i18n.t(lookupErrorCode(data.code).i18nKey),
          } as RevxErrorMessage);
        }
      })
      .catch(async (error) => {
        if (error.response) {
          const { response } = error;
          let errorMessage;
          if (typeof response?.data?.message === 'string') {
            errorMessage =
              response?.data?.message || response?.data?.error || '';
          } else if (typeof response?.data?.msg === 'string') {
            errorMessage = response?.data?.msg;
          } else if (typeof response?.data?.data === 'string') {
            errorMessage = response?.data?.data;
          } else if (typeof response?.data === 'string') {
            errorMessage = response?.data;
          } else {
            errorMessage = `Server error[${response.status}]`;
          }
          if (response.status === 401) {
            localStorage.removeItem(Authorization);
            if (
              shouldRedirectToLoginOn401(
                window.location.pathname,
                window.location.search
              )
            ) {
              window.location.href = `${window.location.origin}/login`;
              this.showError({ errorCode: lookupErrorCode(401) });
            }
          } else if (response.status === 502) {
            if (
              !JSON.parse(localStorage.getItem(SYSTEM_UNAVAILABLE_STATUS_KEY))
            ) {
              window.location.href = `${window.location.origin}/unavailable`;
            }
            localStorage.setItem(SYSTEM_UNAVAILABLE_STATUS_KEY, true);
            return;
          } else if (response.status >= 400) {
            const revxError: RevxErrorMessage = {
              errorCode: lookupErrorCode(response.status),
              originalMessage: errorMessage,
              message: i18n.t(lookupErrorCode(response.status).i18nKey),
            };
            reject(revxError);
            if (error.config.useGlobalErrorHandler !== false) {
              this.showError(revxError, error.config.showOriginalError);
            }
            return;
          }

          reject({
            errorCode: lookupErrorCode(response.status),
            originalMessage: errorMessage,
            message: i18n.t(lookupErrorCode(response.status).i18nKey),
          } as RevxErrorMessage);
          return;
        }

        reject({
          errorCode: lookupErrorCode(-1),
          originalMessage: toErrorText(error),
          message: i18n.t(lookupErrorCode(-1).i18nKey),
        } as RevxErrorMessage);
      });
  }

  showError(
    errorMessage: RevxErrorMessage,
    showOriginalError: boolean = false
  ) {
    // TODO: originalMessage can be displayed when toast supports 'Detail' button
    if (showOriginalError) {
      toast.error(errorMessage.originalMessage);
    } else if (errorMessage.message) {
      toast.error(errorMessage.message);
    } else if (errorMessage.errorCode) {
      toast.error(i18n.t(errorMessage.errorCode.i18nKey));
    }
  }
}

/**
 * originalMessage is rendered as-is by the toast, so it must stay a string.
 * Failures without a response carry an Error (or worse, a null-prototype
 * object) that React cannot render.
 */
function toErrorText(error) {
  if (typeof error === 'string') {
    return error;
  }
  if (typeof error?.message === 'string') {
    return error.message;
  }
  try {
    return String(error);
  } catch {
    return '';
  }
}

/**
 * To switch the endpoint from different env, we use the apiTag to represent api calls for backend.
 * And here replace the apiTag by current env's endpoint url
 *
 * @param path relative api path
 * @returns real endpoint path
 */
function buildPath(path: string) {
  const apiTag = '/api';
  return `${process.env.REACT_APP_API_URL}${path.replace(apiTag, '')}`;
}

/**
 * Puts the unit the user is viewing in on the URL, for every method.
 *
 * This used to be an `x-measurement-unit` header. A header is not part of the endpoint's contract
 * and does not survive anything that rebuilds the request - a proxy, a retry, a service-to-service
 * hop, a link the browser navigates to - and the API falls back to imperial when it goes missing,
 * so a metric user is served inches with nothing failing anywhere to say so.
 *
 * Writes also carry `enteredUnit` in the body, which is a different question: this says which unit
 * to render the reply in, `enteredUnit` says which unit the numbers being submitted were typed in.
 * A form filled in inches and saved after a switch to metric has to answer those differently.
 *
 * `unit` overrides the user's own setting, for a request made from a form that carries its own
 * unit selector - see `MeasurementUnitScope`. Callers outside any form leave it out.
 */
export function withMeasurementUnit(url: string, unit?: MeasurementUnitEnum) {
  // Idempotent: some of these URLs come back from the API already carrying the parameter, and a
  // second copy would be the one a caller reading the query string last happened to see.
  if (new RegExp(`[?&]${MEASUREMENT_UNIT_PARAM}=`).test(url)) {
    return url;
  }
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${MEASUREMENT_UNIT_PARAM}=${encodeURIComponent(
    unit ?? getEffectiveUnit()
  )}`;
}

/**
 * Builds a URL for an endpoint the browser opens directly - a PDF, an Excel export - rather than
 * fetches through this client.
 *
 * Such a request carries no headers of ours, which is why the token rides in the query string.
 *
 * A document whose sizes or weights are rendered server-side also has to say which unit to render
 * them in, and does so by passing `measurementUnit` in `params` - see `MEASUREMENT_UNIT_PARAM`.
 * Inside a form, pass that form's own unit (`useMeasurementUnit().unit`) rather than
 * `getEffectiveUnit()`, so a quote printed from a form filled in millimetres prints in
 * millimetres. Only those endpoints declare it: a sheet of prices has no measurements
 * in it and asking it for a unit says the endpoint does something it does not.
 *
 * Values are URL-encoded; `undefined` and `null` parameters are dropped.
 */
export const buildDocumentUrl = (
  path: string,
  params: Record<string, string | number | boolean | undefined | null> = {}
) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.set(key, String(value));
    }
  });
  query.set('authorization', localStorage.getItem(Authorization) ?? '');
  return `${buildPath(path)}?${query.toString()}`;
};

function generalApi(method: string) {
  return function <K>(
    path: string,
    params?: Params,
    headers?: any,
    config?: AxiosRequestConfig
  ): K {
    return new Api()[method](path, params, headers, config);
  };
}

function generalEventSource() {
  return function <K>(path: string, header?: any): K {
    const orgId = localStorage.getItem('org');
    const date = new Date();
    const headers = {
      ...defaultHeaders,
      Authorization: localStorage.getItem(Authorization) ?? '',
      timezoneOffset: -(date.getTimezoneOffset() / 60),
      ...header,
    };
    if (orgId) {
      headers['X-Tenant-ID'] = orgId;
    }
    return new EventSourcePolyfill(buildPath(path), {
      headers,
      heartbeatTimeout: 180000,
    });
  };
}

const api = {
  get: generalApi('get'),
  put: generalApi('put'),
  post: generalApi('post'),
  delete: generalApi('delete'),
  eventsource: generalEventSource(),
};

window.addEventListener('unhandledrejection', (event) => {
  event.preventDefault();
  // TODO: add monitoring in the future
  console.error('unhandledrejection', event); // eslint-disable-line
  console.error(event?.reason?.stack);
});

export default api;
